import React, { useContext, useEffect, useState } from 'react';

import { DeviceType } from '@amzn/monera-app-core';
import { PageContext, ProductTwisterPlus, SubsBuyBox } from '@amzn/monera-app-core-components';
import { isBrowser } from '@amzn/monera-app-core/lib/utils/env';

import { compareClickHandler, triggerPageUpdate } from './clientSideUtils';
import {
  BuyBoxContainer,
  LeftDummyContainer,
  MobileBuyBoxContainer,
  ParentWrapperContainer,
  StyledDivider
} from './styles';
import { TwisterBuyBoxProps } from './types';

const MobileBuyBox = (props: TwisterBuyBoxProps) => {
  return (
    <MobileBuyBoxContainer id={props.id}>
      <ProductTwisterPlus {...props.twister} isLoading={props.isLoading} compareClickHandler={compareClickHandler} />
      <SubsBuyBox {...props.buyBox} />
    </MobileBuyBoxContainer>
  );
};

/**
 * Buy box with twister that hides when buy box props are empty.
 * @param props
 * @constructor
 */
export const TwisterBuyBox = (props: TwisterBuyBoxProps) => {
  const triggerPageUpdateHookDeps = [props.buyBox && props.buyBox.callToAction && props.buyBox.callToAction.url];
  // Triggering page update to refresh the persistent navigation widget when ASIN or the checkout url changes.
  useEffect(() => {
    triggerPageUpdate();
  }, triggerPageUpdateHookDeps);

  const deviceType = useContext(PageContext).deviceType;
  if (Object.keys(props.buyBox).length === 0) {
    return null;
  }
  return deviceType === DeviceType.Mobile || deviceType === DeviceType.MShop ? (
    <MobileBuyBox {...props} />
  ) : (
    <DesktopBuyBox {...props} />
  );
};

const DesktopBuyBox = (props: TwisterBuyBoxProps) => {
  const deviceType = useContext(PageContext).deviceType;
  const getSize = () => ({
    height: isBrowser() ? window.innerHeight : undefined,
    width: isBrowser() ? window.innerWidth : undefined
  });
  const [windowSize, setWindowSize] = useState<{ width: number | undefined; height: number | undefined }>({
    width: undefined,
    height: undefined
  });
  useEffect(() => {
    if (!isBrowser()) {
      return;
    }
    const handleResize = () => {
      setWindowSize(getSize());
    };
    setWindowSize(getSize());
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { showPaginationButtons, ...twisterProps } = props.twister;
  let showArrows = showPaginationButtons;
  // Temporary fix to not show the arrows in carousel when screenwidth is >= 1532 and there are 3 items in carousel.
  // Will remove this logic when the carousel issue is fixed.
  if (deviceType === DeviceType.Desktop) {
    if (
      windowSize.width &&
      windowSize.width >= 1532 &&
      props.twister.dimensionValues &&
      props.twister.dimensionValues[0].length === 3
    ) {
      showArrows = false;
    } else {
      showArrows = true;
    }
  }
  return (
    <ParentWrapperContainer>
      <LeftDummyContainer />
      <BuyBoxContainer id={props.id}>
        {Object.keys(props.twister).length ? (
          <>
            <ProductTwisterPlus
              {...twisterProps}
              isLoading={props.isLoading}
              showPaginationButtons={showArrows}
              compareClickHandler={compareClickHandler}
            />
            <StyledDivider margin={'0px -24px'} />
          </>
        ) : null}
        <SubsBuyBox {...props.buyBox} />
      </BuyBoxContainer>
    </ParentWrapperContainer>
  );
};
